// src/pages/customer/CustomerManagement.js
// Página para gestionar clientes con listado, paginación, búsqueda y modal de creación

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Table, Modal, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { getCustomers, createCustomer, updateCustomer } from '../../services/customerApi';
import { createPet, getPets } from '../../services/petApi';
import CustomerForm from './CustomerForm';
import PetForm from '../pet/PetForm';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const CustomerManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Estados para el listado
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();
  const [customersWithPets, setCustomersWithPets] = useState(new Set());
  
  // Estados para paginación y búsqueda
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  
  // Estados para el modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPetModal, setShowPetModal] = useState(false);
  const [showPostCreateOptionsModal, setShowPostCreateOptionsModal] = useState(false);
  const [newlyCreatedClient, setNewlyCreatedClient] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Cargar información de mascotas para determinar qué clientes tienen mascotas
  const loadPetsInfo = useCallback(async () => {
    try {
      // Cargar todas las mascotas (con un límite razonable)
      // Si hay muchas mascotas, podríamos optimizar esto más adelante
      const { ok, status, data } = await getPets({ page: 1, per_page: 1000 });
      
      if (ok && (status === 200 || data?.code === 200)) {
        const petsData = data.data || data.message || [];
        const petsList = Array.isArray(petsData) ? petsData : (petsData.pets || []);
        
        // Crear un Set con los IDs de clientes que tienen mascotas
        const clientsWithPetsSet = new Set();
        petsList.forEach(pet => {
          if (pet.id_client) {
            clientsWithPetsSet.add(pet.id_client);
          }
        });
        
        setCustomersWithPets(clientsWithPetsSet);
      }
    } catch (error) {
      console.error('Error cargando información de mascotas:', error);
      // No mostramos error al usuario ya que esto es solo para el indicador
    }
  }, []);

  // Cargar clientes
  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        page: currentPage,
        per_page: perPage,
        q: searchQuery.trim()
      };
      
      const { ok, status, data, total } = await getCustomers(params);
      
      if (ok && (status === 200 || data?.code === 200)) {
        setTotalCustomers(data?.total);
        const responseData = data.data || data.message || [];
        setCustomers(responseData.customers || responseData);
        
        // Manejar metadatos de paginación
        if (data?.per_page) {
          setTotalPages(data?.total_pages || 1);
          setTotalCustomers(data?.total);
        } else if (responseData.total) {
          setTotalPages(Math.ceil(responseData.total / perPage));
          setTotalCustomers(1);
        }
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error cargando clientes';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error cargando clientes:', error);
      showAlert('Error inesperado al cargar clientes', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, searchQuery]);

  // Cargar información de mascotas al montar el componente
  useEffect(() => {
    loadPetsInfo();
  }, [loadPetsInfo]);

  // Cargar clientes al montar el componente y cuando cambien los parámetros
  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Manejar búsqueda con debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadCustomers();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Manejar cambio de elementos por página
  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // Manejar búsqueda
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Abrir modal de creación
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
  };

  // Cerrar modal de creación
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedCustomer(null);
  };

  // Abrir modal de edición
  const handleOpenEditModal = (customer) => {
    console.log(customer);
    setSelectedCustomer(customer);
    setShowEditModal(true);
  };

  // Abrir modal de creación de mascota
  const handleCreatePetForCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowPetModal(true);
  };

  // Cerrar modal de mascota
  const handleClosePetModal = () => {
    setShowPetModal(false);
    setSelectedCustomer(null);
  };

  // Cerrar modal de opciones post-crear cliente
  const handleClosePostCreateOptions = () => {
    setShowPostCreateOptionsModal(false);
    setNewlyCreatedClient(null);
  };

  // Crear mascota desde modal post-crear cliente
  const handleOpenCreatePetFromNewClient = () => {
    if (newlyCreatedClient) {
      setSelectedCustomer(newlyCreatedClient);
      setShowPostCreateOptionsModal(false);
      setShowPetModal(true);
      setNewlyCreatedClient(null);
    }
  };

  // Crear cita: navegar a agenda
  const handlePostCreateChooseCita = () => {
    setShowPostCreateOptionsModal(false);
    setNewlyCreatedClient(null);
    navigate('/agendamiento-citas');
  };

  // Manejar creación de cliente
  const handleCreateCustomer = async (customerData) => {
    try {
      setModalLoading(true);
      
      const { ok, status, data } = await createCustomer(customerData);
      
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Cliente creado exitosamente', 'success');
        setShowCreateModal(false);
        const createdClient = data?.data || data?.message || data;
        setNewlyCreatedClient(createdClient);
        setShowPostCreateOptionsModal(true);
        loadCustomers();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error creando cliente';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error creando cliente:', error);
      showAlert('Error inesperado al crear cliente', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Manejar actualización de cliente
  const handleUpdateCustomer = async (customerData) => {
    try {
      setModalLoading(true);
      
      const { ok, status, data } = await updateCustomer(selectedCustomer.id_client, customerData);
      
      if (ok && (status === 200 || data?.code === 200)) {
        showAlert(data?.message || 'Cliente actualizado exitosamente', 'success');
        setShowEditModal(false);
        setSelectedCustomer(null);
        // Recargar la lista
        loadCustomers();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error actualizando cliente';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error actualizando cliente:', error);
      showAlert('Error inesperado al actualizar cliente', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Manejar creación de mascota desde cliente
  const handleCreatePetFromCustomer = async (petData) => {
    try {
      setModalLoading(true);
      
      // Agregar el ID del cliente seleccionado
      const petDataWithClient = {
        ...petData,
        id_client: selectedCustomer.id_client
      };
      
      const { ok, status, data } = await createPet(petDataWithClient);
      
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Mascota creada exitosamente', 'success');
        setShowPetModal(false);
        setSelectedCustomer(null);
        // Recargar la lista de clientes y la información de mascotas
        loadCustomers();
        loadPetsInfo();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error creando mascota';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error creando mascota:', error);
      showAlert('Error inesperado al crear mascota', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Generar array de páginas para la paginación
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row>
          <Col>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Gestión de Clientes</h4>
                <Button variant="primary" onClick={handleOpenCreateModal}>
                  <i className="fas fa-plus me-2"></i>
                  Nuevo Cliente
                </Button>
              </Card.Header>
              <Card.Body>

                {/* Controles de búsqueda y filtros */}
                <Row className="mb-3">
                  <Col md={6}>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="fas fa-search"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Buscar por nombre, apellidos, documento, email o WhatsApp..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Select value={perPage} onChange={handlePerPageChange}>
                      <option value={10}>10 por página</option>
                      <option value={20}>20 por página</option>
                      <option value={50}>50 por página</option>
                      <option value={100}>100 por página</option>
                    </Form.Select>
                  </Col>
                  <Col md={3} className="d-flex align-items-center">
                    <small className="text-muted">
                      Total: {totalCustomers} clientes
                    </small>
                  </Col>
                </Row>

                {/* Tabla de clientes */}
                {loading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Nombre Completo</th>
                          <th>Email</th>
                          <th>WhatsApp</th>
                          <th>Documento</th>
                          <th>Marketing</th>
                          <th>Mascotas</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customers.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              {searchQuery ? 'No se encontraron clientes con ese criterio de búsqueda' : 'No hay clientes registrados'}
                            </td>
                          </tr>
                        ) : (
                          customers.map((customer) => {
                            const hasPets = customersWithPets.has(customer.id_client);
                            return (
                            <tr key={customer.id_client}>
                              <td>{customer.id_client}</td>
                              <td>
                                
                                  {customer.name || ''} {customer.surname || ''} {customer.lastname || ''} {customer.second_surname || ''} 
                                
                              </td>
                              <td>{customer.email || 'N/A'}</td>
                              <td>{customer.phone_whatsapp || 'N/A'}</td>
                              <td>{customer.document || 'N/A'}</td>
                              <td>
                                <span className={`badge ${customer.marketing_opt_in ? 'bg-success' : 'bg-secondary'}`}>
                                  {customer.marketing_opt_in ? 'Sí' : 'No'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${hasPets ? 'bg-info' : 'bg-secondary'}`}>
                                  {hasPets ? (
                                    <>
                                      <i className="fas fa-paw me-1"></i>
                                      Sí
                                    </>
                                  ) : (
                                    'No'
                                  )}
                                </span>
                              </td>
                              <td>
                                <Button 
                                  variant="outline-info" 
                                  size="sm" 
                                  className="me-2"
                                  onClick={() => navigate(`/perfil-cliente/${customer.id_client}`)}
                                  title="Ver perfil completo del cliente"
                                >
                                  <i className="fas fa-user me-1"></i>
                                  Perfil
                                </Button>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  className="me-2"
                                  onClick={() => handleOpenEditModal(customer)}
                                >
                                  <i className="fas fa-edit me-1"></i>
                                  Editar
                                </Button>
                                <Button 
                                  variant="outline-success" 
                                  size="sm" 
                                  className="me-2"
                                  onClick={() => handleCreatePetForCustomer(customer)}
                                >
                                  <i className="fas fa-paw me-1"></i>
                                  Mascota
                                </Button>
                              </td>
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>

                    {/* Paginación */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          <small className="text-muted">
                            Página {currentPage} de {totalPages}
                          </small>
                        </div>
                        <div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="me-2"
                          >
                            <i className="fas fa-chevron-left"></i>
                          </Button>
                          
                          {generatePageNumbers().map(page => (
                            <Button
                              key={page}
                              variant={page === currentPage ? "primary" : "outline-secondary"}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              className="me-2"
                            >
                              {page}
                            </Button>
                          ))}
                          
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal de creación de cliente */}
        <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Crear Nuevo Cliente</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <CustomerForm
              onSubmit={handleCreateCustomer}
              loading={modalLoading}
            />
          </Modal.Body>
        </Modal>

        {/* Modal de edición de cliente */}
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Editar Cliente</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedCustomer && (
              <CustomerForm
                onSubmit={handleUpdateCustomer}
                loading={modalLoading}
                initialData={selectedCustomer}
                submitText="Actualizar Cliente"
              />
            )}
          </Modal.Body>
        </Modal>

        {/* Modal de creación de mascota desde cliente */}
        <Modal show={showPetModal} onHide={handleClosePetModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Crear Mascota para {selectedCustomer ? `${selectedCustomer.name || ''} ${selectedCustomer.surname || ''}`.trim() : ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedCustomer && selectedCustomer.id_client && (
              <PetForm
                onSubmit={handleCreatePetFromCustomer}
                loading={modalLoading}
                initialData={{ id_client: selectedCustomer.id_client }}
                submitText="Crear Mascota"
              />
            )}
          </Modal.Body>
        </Modal>

        {/* Modal opciones post-crear cliente */}
        <Modal show={showPostCreateOptionsModal} onHide={handleClosePostCreateOptions} centered>
          <Modal.Header closeButton>
            <Modal.Title>Cliente creado</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3">
              ¿Qué deseas hacer ahora con {newlyCreatedClient ? `${(newlyCreatedClient.name || '')} ${(newlyCreatedClient.surname || '')}`.trim() || 'el cliente' : 'el cliente'}?
            </p>
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="primary" onClick={handlePostCreateChooseCita}>
                <i className="fas fa-calendar-check me-2"></i>
                Crear cita
              </Button>
              <Button variant="outline-primary" onClick={handleOpenCreatePetFromNewClient} disabled={!newlyCreatedClient?.id_client}>
                <i className="fas fa-paw me-2"></i>
                Crear mascota
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Modal de Alerta Temporal */}
        <AlertModal
          show={showAlertModal}
          onHide={hideAlert}
          message={alertMessage}
          type={alertType}
        />
      </Container>
    </>
  );
};

export default CustomerManagement;
